import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Loader2 } from "lucide-react";
import { useEditNote, type NoteRow } from "@/lib/api";
import { useToast } from "@/hooks/use-toast";
import { trackEvent } from "@/lib/trackEvent";

// CSR-771 — Edit Note modal. Same form as Add but pre-populated, sends only
// the fields that actually changed so the activity-log `details.fields` array
// reflects the user's intent. `createdAt` is preserved server-side; the
// `is_edited = true` and bumped `updated_at` are also server-driven.

const TITLE_MAX = 200;
const TITLE_COUNTER_THRESHOLD = 150;

interface EditNoteModalProps {
  note: NoteRow;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function EditNoteModal({ note, open, onOpenChange }: EditNoteModalProps) {
  const [title, setTitle] = useState(note.title);
  const [details, setDetails] = useState(note.details ?? "");
  const [showError, setShowError] = useState(false);
  const editNote = useEditNote();
  const { toast } = useToast();

  // Re-sync state when a different note opens or the underlying row updates.
  useEffect(() => {
    if (open) {
      setTitle(note.title);
      setDetails(note.details ?? "");
      setShowError(false);
    }
  }, [open, note.id, note.title, note.details]);

  const handleTitleChange = (value: string) => {
    if (value.length > TITLE_MAX) {
      setTitle(value.slice(0, TITLE_MAX));
    } else {
      setTitle(value);
    }
    if (showError && value.trim().length > 0) {
      setShowError(false);
    }
  };

  const submit = () => {
    const t = title.trim();
    if (!t) {
      setShowError(true);
      trackEvent("Note Validation Error", {
        scope: note.policyId ? "policy" : "insured",
      });
      return;
    }
    const detailsTrimmed = details.trim();
    const detailsValue = detailsTrimmed.length > 0 ? detailsTrimmed : null;

    // Send only changed fields so the activity-log `fields` array is accurate.
    const patch: { noteId: string; title?: string; details?: string | null } = {
      noteId: note.id,
    };
    if (t !== note.title) patch.title = t;
    if (detailsValue !== (note.details ?? null)) patch.details = detailsValue;

    if (patch.title === undefined && patch.details === undefined) {
      // Nothing changed — close without firing a request.
      onOpenChange(false);
      return;
    }

    editNote.mutate(patch, {
      onSuccess: () => {
        toast({
          title: "Note Updated",
          description: "Your changes have been saved.",
        });
        onOpenChange(false);
        const elapsed = Date.now() - new Date(note.createdAt as unknown as string).getTime();
        trackEvent("Note Edited", {
          note_id: note.id,
          time_since_creation_ms: elapsed,
        });
      },
      onError: (err) => {
        const status = (err as { status?: number })?.status ?? 0;
        const errorType =
          status >= 500 ? "server" : status === 422 ? "validation" : "network";
        toast({
          title: "Failed to update note. Please try again.",
          variant: "destructive",
        });
        trackEvent("Note Save Failed", {
          scope: note.policyId ? "policy" : "insured",
          error_type: errorType,
        });
      },
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-md"
        onKeyDown={(e) => {
          if (e.key !== "Enter") return;
          // Plain Enter in the textarea adds a newline; Cmd/Ctrl+Enter still
          // submits from anywhere. Plain Enter from the title input (or other
          // non-textarea focus) submits.
          const isTextarea =
            (e.target as HTMLElement | null)?.tagName === "TEXTAREA";
          if ((e.metaKey || e.ctrlKey) || !isTextarea) {
            e.preventDefault();
            submit();
          }
        }}
      >
        <DialogHeader>
          <DialogTitle>Edit Note</DialogTitle>
          <DialogDescription>Update this note on the activity timeline.</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="edit-note-title">
              Title <span className="text-destructive">*</span>
            </Label>
            <Input
              id="edit-note-title"
              autoFocus
              placeholder="Note title..."
              value={title}
              maxLength={TITLE_MAX}
              onChange={(e) => handleTitleChange(e.target.value)}
              className="rounded-lg h-10"
              aria-invalid={showError}
              aria-describedby={showError ? "edit-note-title-error" : undefined}
              data-testid="edit-note-title"
            />
            {showError && (
              <p
                id="edit-note-title-error"
                role="alert"
                className="text-xs text-destructive"
                data-testid="edit-note-title-error"
              >
                Title is required
              </p>
            )}
            {title.length >= TITLE_COUNTER_THRESHOLD && (
              <p className="text-xs text-muted-foreground" data-testid="edit-note-title-counter">
                {title.length}/{TITLE_MAX}
              </p>
            )}
          </div>
          <div className="space-y-2">
            <Label htmlFor="edit-note-details">Details</Label>
            <Textarea
              id="edit-note-details"
              placeholder="Write your note here..."
              value={details}
              onChange={(e) => setDetails(e.target.value)}
              className="min-h-[96px] resize-none rounded-lg"
              data-testid="edit-note-details"
            />
          </div>
        </div>

        <div className="flex justify-end gap-2">
          <Button
            variant="outline"
            className="rounded-full"
            onClick={() => onOpenChange(false)}
            disabled={editNote.isPending}
            data-testid="edit-note-cancel"
          >
            Cancel
          </Button>
          <Button
            className="rounded-full gap-2"
            onClick={submit}
            // CSR-790 D-1 — see AddNoteModal.tsx for rationale. Same fix.
            disabled={editNote.isPending}
            data-testid="edit-note-submit"
          >
            {editNote.isPending && <Loader2 className="w-4 h-4 animate-spin" />}
            Save Changes
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
